import { useInfiniteQuery } from '@tanstack/react-query';

import { parseDateRange } from '@/components/data-table/filters-v1/components/data-table-date-range-picker-v2-filter';
import { FilterValues } from '@/hooks/use-filter-values';
import { flattenInfinitePages, getNextPageParam } from '@/lib/constants/pagination';
import { mapInvoiceStatusToDTO } from '@/modules/common/mappers/invoice-status.mapper';
import { GetInvoicesFilter } from '@/modules/finance/invoices/types';
import { formatDateToISO, normalizeToStartOfDayUTC } from '@/utils/date-formatters';

import { getInvoices } from '../api';
import { InvoiceStatus } from '../types';

/** Base query key for the invoices list. Combine with filters for the full key. */
export const INVOICES_QUERY_KEY = 'invoices';

export const useInvoices = ({ filters }: { filters: FilterValues['values'] }) => {
  return useInfiniteQuery({
    queryKey: [INVOICES_QUERY_KEY, filters],
    queryFn: ({ pageParam }) => {
      const apiFilters = mapInvoicesFilters(filters);
      return getInvoices({ cursor: pageParam, filters: apiFilters });
    },
    getNextPageParam,
    initialPageParam: undefined as number | undefined,
    staleTime: 10 * 10 * 1000, // 10 minutes
    select: flattenInfinitePages,
  });
};

/**
 * Maps UI filter values to API request format.
 * Transforms filter names and values to match backend expectations.
 */
export const mapInvoicesFilters = (filters: FilterValues['values']): GetInvoicesFilter => {
  const apiFilters: GetInvoicesFilter = {};

  const dates = parseDateRange(
    typeof filters.billingPeriod === 'string' ? filters.billingPeriod : ''
  );

  if (dates) {
    apiFilters.startDate = formatDateToISO(normalizeToStartOfDayUTC(dates.startDate));
    apiFilters.endDate = formatDateToISO(normalizeToStartOfDayUTC(dates.endDate));
  }

  // Search term
  if (filters.searchString && typeof filters.searchString === 'string') {
    apiFilters.searchTerm = filters.searchString;
  }

  // Status filter - convert to DTO enums
  if (filters.status) {
    const statusArray = Array.isArray(filters.status) ? filters.status : [filters.status];
    apiFilters.statuses = statusArray.map((s: InvoiceStatus) => mapInvoiceStatusToDTO(s));
  }

  // Project filter - convert to number array
  if (filters.project) {
    const projectArray = Array.isArray(filters.project) ? filters.project : [filters.project];
    apiFilters.projectIds = projectArray.map((id: string | number) => Number(id));
  }

  // Client filter - convert to number array
  if (filters.client) {
    const clientArray = Array.isArray(filters.client) ? filters.client : [filters.client];
    apiFilters.clientIds = clientArray.map((id: string | number) => Number(id));
  }

  return apiFilters;
};
